import {
  FoodDetailsBody,
  FoodDetailsContent,
  FoodDetailsHeader,
  FoodDetailsInfo,
  IngredientList,
  IngredientListTitle,
  StyledFoodDetails,
} from "./styled"
import { BackButton } from "components/Common/BackButton"
import Button from "components/Buttons/Button"
import { FoodListDivider } from "components/foodItem/FoodListItem/styled"
import FoodImage from "../share/FoodImage"
import { TopNavWrapper } from "components/Layouts/TopNavWrapper"
import { FOOD_DETAILS_BODY_ID } from "config"
import { useState } from "react"
import { useBackToHome } from "features/meal/hooks"
import ChevronLeftBtn from "components/Icons/ChevronLeftBtn"
import useScheduleFormSection from "features/schedule/hooks/useScheduleFormSection"
import { useCreateSchedule } from "features/schedule/hooks/CRUDSchedule"
import ScheduleConfigFormSection from "components/foodDetails/share/ScheduleConfigFormSection"
import PopConfirm from "components/PopConfirm"
import { validateScheduleConfig } from "utils/validator"
import useGramFromIngredients from "features/food/hooks/useGramFromIngredients"
import { IMyFood } from "model/food/types/myfood.types"
import useMyFoodDetailsForm from "features/food/hooks/myFood/useMyFoodDetailsForm"
import useLogMealFromFood from "features/meal/hooks/logMeal/useLogMealFromFood"
import ViewOnlyIngredient from "../share/ViewOnlyIngredient"
import MyFoodNameAndPortion from "../share/MyFoodNameAndPortion"
import AppStyleCtaBox from "components/Common/AppStyleCtaBox"
import { FoodCreationMethod } from "model/food/types/mealPayload.types"
import EditNutrientForm from "../share/EditNutrientForm"
import EditableNutrientInformation from "../share/EditableNutrientInformation"

interface Iprops {
  onBack: () => void
  food: IMyFood
  foodCreationMethod?: FoodCreationMethod
}

const MyFoodDetailsWithSchedule = (props: Iprops) => {
  const { onBack, food, foodCreationMethod } = props
  const [displaySection, setDisplaySection] = useState<
    "FOOD_FORM" | "EDIT_NUTRITION_FORM"
  >("FOOD_FORM")

  const backToHome = useBackToHome()
  const { handleLogMealFromFood, logMealLoading } =
    useLogMealFromFood(backToHome)
  const { myFood, onPortionChange, onUnitChange, updateFoodAsManualNutrition } =
    useMyFoodDetailsForm(food)

  const [isPortionValid, setIsPortionValid] = useState(true)
  const [hasSchedule, setHasSchedule] = useState(false)
  const [isPopConfirmScheduleOpen, setIsPopConfirmScheduleOpen] =
    useState(false)

  const {
    onDayOfWeekChange,
    onSchedulePeriodChange,
    onScheduleTypeChange,
    scheduleConfig,
    isNoEndDate,
    onNoEndDateChange,
    getCreateSchedulePayloadFromFood,
  } = useScheduleFormSection()

  const { handleCreateSchedule, createScheduleLoading } =
    useCreateSchedule(backToHome)

  const onPortionChangeHandler = (value: number) => {
    onPortionChange(value)
    setIsPortionValid(true)
  }

  const gram = useGramFromIngredients(myFood)

  const onSubmit = (food: IMyFood) => {
    if (hasSchedule) {
      const schedulePayload = getCreateSchedulePayloadFromFood(food)

      if (schedulePayload) {
        handleCreateSchedule(schedulePayload, foodCreationMethod)
      }
    } else {
      handleLogMealFromFood(food, foodCreationMethod)
    }
  }

  const isScheduleValid = hasSchedule
    ? validateScheduleConfig(scheduleConfig, !isNoEndDate)
    : true

  if (!myFood) {
    return null
  }

  if (displaySection === "EDIT_NUTRITION_FORM") {
    return (
      <EditNutrientForm
        onClose={() => {
          setDisplaySection("FOOD_FORM")
        }}
        meal={myFood}
        onSubmit={updateFoodAsManualNutrition}
      />
    )
  }

  return (
    <StyledFoodDetails>
      <FoodDetailsContent>
        <FoodDetailsBody id={FOOD_DETAILS_BODY_ID}>
          <FoodImage previewImageUrl={myFood.previewImageUrl}>
            <TopNavWrapper>
              <FoodDetailsHeader>
                <BackButton
                  $disabled={logMealLoading}
                  onClick={() => {
                    if (!logMealLoading) {
                      onBack()
                    }
                  }}
                >
                  <ChevronLeftBtn />
                </BackButton>
              </FoodDetailsHeader>
            </TopNavWrapper>
          </FoodImage>
          <FoodDetailsInfo>
            <MyFoodNameAndPortion
              food={myFood}
              unitValue={myFood.unit}
              portionValue={myFood.portion}
              onPortionChange={onPortionChangeHandler}
              onUnitChange={onUnitChange}
              setIsPortionValid={setIsPortionValid}
              calories={myFood.calorie}
              gram={gram}
            />

            <EditableNutrientInformation
              entry={myFood}
              setEditMode={() => {
                setDisplaySection("EDIT_NUTRITION_FORM")
              }}
            />

            <ScheduleConfigFormSection
              scheduleConfig={scheduleConfig}
              onDayOfWeekChange={onDayOfWeekChange}
              onSchedulePeriodChange={onSchedulePeriodChange}
              onScheduleTypeChange={onScheduleTypeChange}
              isNoEndDate={isNoEndDate}
              onNoEndDateChange={onNoEndDateChange}
              hasSchedule={hasSchedule}
              setHasSchedule={setHasSchedule}
            />

            {myFood.ingredients.length ? (
              <IngredientList>
                <IngredientListTitle>Ingredients</IngredientListTitle>{" "}
                {myFood.ingredients.map((ingredient, index) => (
                  <div key={index}>
                    <ViewOnlyIngredient
                      ingredient={ingredient}
                      isManualNutrition={myFood.isManualNutrition}
                    />
                    {index !== (myFood.ingredients?.length ?? 1) - 1 && (
                      <FoodListDivider />
                    )}
                  </div>
                ))}
              </IngredientList>
            ) : undefined}
          </FoodDetailsInfo>
        </FoodDetailsBody>
        <AppStyleCtaBox>
          <Button
            onClick={() => {
              if (hasSchedule) {
                setIsPopConfirmScheduleOpen(true)
              } else {
                onSubmit(myFood)
              }
            }}
            style={{ width: "100%" }}
            $type="primary"
            disabled={!isPortionValid || !isScheduleValid}
            loading={logMealLoading}
          >
            Log this item
          </Button>
        </AppStyleCtaBox>
      </FoodDetailsContent>

      <PopConfirm
        cancelText="No"
        confirmText="Yes"
        isOpen={isPopConfirmScheduleOpen}
        title="Add Scheduled Meal?"
        onCancel={() => setIsPopConfirmScheduleOpen(false)}
        onConfirm={() => {
          onSubmit(myFood)
        }}
        type="info"
        loading={createScheduleLoading}
      >
        <div style={{ textAlign: "center" }}>
          This meal will be logged automatically on your selected days. Do you
          want to proceed?
        </div>
      </PopConfirm>
    </StyledFoodDetails>
  )
}

export default MyFoodDetailsWithSchedule
